import { PushNotifications } from '@capacitor/push-notifications';
import history from '@history';
import { pushNotificationsApi } from 'app/services/api';
import { getDeviceUuid } from 'app/services/secureTokenStore';

import { logger } from 'utils/logger';

import { getDeepLinkPath, getDeviceMetadata, getPushPlatform } from './nativePush.utils';

class NativePush {
  #deviceUuid;
  #isRegistered = false;
  #onPushToken = null;

  #onRegistration = async token => {
    try {
      await this.#onPushToken?.({ pushToken: token.value, pushPlatform: getPushPlatform() });
    } catch (error) {
      logger.error('[NativePush] device registration failed', error);
    }
  };

  #onRegistrationError = error => {
    logger.error('[NativePush] registration error', error);
  };

  #onActionPerformed = action => {
    const path = getDeepLinkPath(action.notification?.data);

    if (path) {
      history.push(path);
    }
  };

  #onNotificationReceived = notification => {
    logger.info('[NativePush] foreground push received', notification?.data);
  };

  register = async ({ onPushToken = null } = {}) => {
    if (this.#isRegistered) {
      return;
    }

    this.#deviceUuid = getDeviceUuid();
    this.#onPushToken = onPushToken;

    const permission = await PushNotifications.requestPermissions();

    if (permission.receive !== 'granted') {
      return;
    }

    await PushNotifications.addListener('registration', this.#onRegistration);
    await PushNotifications.addListener('registrationError', this.#onRegistrationError);
    await PushNotifications.addListener('pushNotificationActionPerformed', this.#onActionPerformed);
    await PushNotifications.addListener('pushNotificationReceived', this.#onNotificationReceived);

    await PushNotifications.register();

    this.#isRegistered = true;
  };

  unregister = async () => {
    if (!this.#isRegistered) {
      return;
    }

    if (this.#deviceUuid) {
      await pushNotificationsApi
        .unregisterDevice(this.#deviceUuid)
        .catch(error => logger.error('[NativePush] device unregister failed', error));
    }

    await PushNotifications.removeAllListeners();

    this.#isRegistered = false;
  };

  sendHeartbeat = async () => {
    if (!this.#isRegistered || !this.#deviceUuid) {
      return;
    }

    const metadata = await getDeviceMetadata();

    await pushNotificationsApi
      .sendDeviceHeartbeat({
        deviceUuid: this.#deviceUuid,
        appNativeVersion: metadata.appNativeVersion,
        osVersion: metadata.osVersion ?? null,
        bundleVersion: metadata.bundleVersion,
      })
      .catch(error => logger.error('[NativePush] heartbeat failed', error));
  };
}

export default NativePush;
